import { Calendar, DollarSign, FileText, Users, Wallet } from 'lucide-react';
import { useState } from 'react';

import { useGetAccountsReceivable } from '~entities/dashboard/api/get-accounts-receivable';
import { useGetCollectedRevenueMonthlyTrend } from '~entities/dashboard/api/get-collected-revenue-monthly-trend';
import { useGetActiveClassesWeekComparison } from '~entities/dashboard/api/get-active-classes-week-comparison';
import { useGetEnrollmentPercentageOverLastMonth } from '~entities/dashboard/api/get-enrollment-percentage-over-last-month';
import { useGetEnrollmentToStudentRatio } from '~entities/dashboard/api/get-enrollment-to-student-ratio';
import { useGetManualAccountsReceivable } from '~entities/dashboard/api/get-manual-accounts-receivable';
import { useGetStudentEnrolmentPercentage } from '~entities/dashboard/api/get-student-enrolment-percentage';
import { useGetTotalActiveClasses } from '~entities/dashboard/api/get-total-active-classes';
import { useGetTotalEnrollmentsOverTime } from '~entities/dashboard/api/get-total-enrollments-over-time';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import {
  getCollectedRevenueMonthlyTrendEndDate,
  getCollectedRevenueMonthlyTrendStartDate,
  getCurrentMonthTrend,
} from '~features/new-dashboard/helpers/collected-revenue-monthly-trend';

import { AccountReceivableModal } from '~features/new-dashboard/ui/account-receivable-model';
import { AccountReceivablePieChart } from '~features/new-dashboard/ui/account-receivable-pie-chart';
import { EnrollmentDetailModal } from '~features/new-dashboard/ui/enrollment-detail-modal';
import { ManualAccountReceivablePieChart } from '~features/new-dashboard/ui/manual-account-receivable-pie-chart';
import { RevenueDetailModal } from '~features/new-dashboard/ui/revenue-detail-modal';
import { StatCard } from '~features/new-dashboard/ui/stat-card';

import { useGetCurrency } from '~shared/api/currency-api';
import { formatCurrencyAmount } from '../helpers/format-currency';
import {
  formatLastMonthStudentUnitsComparison,
  toMonthOverMonthStatDelta,
} from '../helpers/format-month-over-month-delta';

interface StatsRowProps {
  sessionId: string;
}

export const StatsRow = ({ sessionId }: StatsRowProps) => {
  const [enrollmentModalOpen, setEnrollmentModalOpen] = useState(false);
  const [revenueModalOpen, setRevenueModalOpen] = useState(false);
  const [accountReceivableModalOpen, setAccountReceivableModalOpen] =
    useState(false);
  const [
    manualAccountReceivableModalOpen,
    setManualAccountReceivableModalOpen,
  ] = useState(false);

  const { hasSuperAdminAccess } = useCurrentUserRole();
  const { data: currencyDetails } = useGetCurrency();
  const currencySymbol = currencyDetails?.symbol ?? '$';

  const {
    data: enrollmentToStudentRatio,
    isLoading: isEnrollmentRatioLoading,
  } = useGetEnrollmentToStudentRatio({
    sessionId,
  });

  const {
    data: studentEnrolmentPercentage,
    isLoading: isStudentEnrolmentPercentageLoading,
  } = useGetStudentEnrolmentPercentage({ sessionId });

  const {
    data: totalEnrollmentsOverTime,
    isLoading: isTotalEnrollmentsOverTimeLoading,
  } = useGetTotalEnrollmentsOverTime({ sessionId });

  const { data: totalActiveClasses, isLoading: isTotalActiveClassesLoading } =
    useGetTotalActiveClasses({ sessionId });

  const {
    data: activeClassesWeekComparison,
    isLoading: isActiveClassesWeekComparisonLoading,
  } = useGetActiveClassesWeekComparison({ sessionId });

  const {
    data: enrollmentPercentageOverLastMonth,
    isLoading: isEnrollmentPercentageOverLastMonthLoading,
  } = useGetEnrollmentPercentageOverLastMonth({ sessionId });

  const {
    data: collectedRevenueMonthlyTrend,
    isLoading: isCollectedRevenueMonthlyTrendLoading,
  } = useGetCollectedRevenueMonthlyTrend({
    sessionId,
    startDate: getCollectedRevenueMonthlyTrendStartDate(),
    endDate: getCollectedRevenueMonthlyTrendEndDate(),
  });

  const currentMonthRevenueTrend = getCurrentMonthTrend(
    collectedRevenueMonthlyTrend,
  );

  const { data: accountsReceivable, isLoading: isAccountsReceivableLoading } =
    useGetAccountsReceivable({ sessionId });

  const {
    data: manualAccountsReceivable,
    isLoading: isManualAccountsReceivableLoading,
  } = useGetManualAccountsReceivable({
    enabled: hasSuperAdminAccess,
  });

  const formatCurrency = (value: number) =>
    `${currencySymbol}${formatCurrencyAmount(value)}`;

  return (
    <>
      <div className='grid w-full min-w-0 grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3'>
        <StatCard
          title='Total Active Students'
          value={String(enrollmentToStudentRatio?.totalActiveStudents ?? 0)}
          subtitle={`${totalEnrollmentsOverTime?.totalEnrollments ?? 0} Units`}
          delta={toMonthOverMonthStatDelta(
            studentEnrolmentPercentage,
            studentEnrolmentPercentage != null
              ? formatLastMonthStudentUnitsComparison(
                  studentEnrolmentPercentage.lastMonth,
                  studentEnrolmentPercentage.lastMonthUnits,
                )
              : undefined,
          )}
          icon={<Users className='size-5' />}
          onClick={() => {
            setEnrollmentModalOpen(true);
          }}
          isLoading={
            isEnrollmentRatioLoading ||
            isStudentEnrolmentPercentageLoading ||
            isTotalEnrollmentsOverTimeLoading
          }
        />

        <StatCard
          title='Monthly Active Classes'
          value={totalActiveClasses?.totalClassesEnrolled ?? '0'}
          subtitle={`${activeClassesWeekComparison?.delta != null && activeClassesWeekComparison.delta >= 0 ? '+' : ''}${activeClassesWeekComparison?.delta ?? 0} this week`}
          delta={toMonthOverMonthStatDelta(enrollmentPercentageOverLastMonth)}
          icon={<Calendar className='size-5' />}
          isLoading={
            isTotalActiveClassesLoading ||
            isEnrollmentPercentageOverLastMonthLoading ||
            isActiveClassesWeekComparisonLoading
          }
        />

        {hasSuperAdminAccess && (
          <>
            <StatCard
              title='Collected Revenue (MTD)'
              value={formatCurrency(currentMonthRevenueTrend?.collected ?? 0)}
              badge={{
                label:
                  formatCurrency(currentMonthRevenueTrend?.scheduled ?? 0) +
                  ' scheduled',
                variant: 'success',
              }}
              icon={<DollarSign className='size-5' />}
              tooltip='Collected revenue this month. Scheduled badge shows pre-charge invoices still due today or later this month.'
              onClick={() => {
                setRevenueModalOpen(true);
              }}
              isLoading={isCollectedRevenueMonthlyTrendLoading}
            />
          </>
        )}
      </div>
      <div className='grid w-full min-w-0 gap-4 md:gap-6 lg:grid-cols-2'>
        {hasSuperAdminAccess && (
          <>
            <StatCard
              className='md:h-60'
              title='Accounts Receivable (MTD)'
              value={formatCurrency(accountsReceivable?.totalAccountReceivable ?? 0)}
              icon={<Wallet className='size-5' />}
              onClick={() => {
                setAccountReceivableModalOpen(true);
              }}
              tooltip='Account receivable shows the total amount of money owed to the studio this month. Insight data shows all receivable details student wise.'
              chart={<AccountReceivablePieChart sessionId={sessionId} />}
              isLoading={isAccountsReceivableLoading}
            />
            <StatCard
              className='md:h-60'
              title='Manual invoice receivables (MTD)'
              value={formatCurrency(manualAccountsReceivable?.totalAccountReceivable ?? 0)}
              icon={<FileText className='size-5' />}
              onClick={() => {
                setManualAccountReceivableModalOpen(true);
              }}
              tooltip='All-time outstanding balances from manual invoices not linked to classes or sessions.'
              chart={<ManualAccountReceivablePieChart />}
              isLoading={isManualAccountsReceivableLoading}
            />
          </>
        )}
      </div>

      {enrollmentModalOpen && (
        <EnrollmentDetailModal
          open={enrollmentModalOpen}
          onOpenChange={setEnrollmentModalOpen}
          sessionId={sessionId}
        />
      )}

      {revenueModalOpen && (
        <RevenueDetailModal
          open={revenueModalOpen}
          onOpenChange={setRevenueModalOpen}
          sessionId={sessionId}
        />
      )}

      {accountReceivableModalOpen && (
        <AccountReceivableModal
          open={accountReceivableModalOpen}
          onOpenChange={setAccountReceivableModalOpen}
          sessionId={sessionId}
        />
      )}

      {manualAccountReceivableModalOpen && (
        <AccountReceivableModal
          open={manualAccountReceivableModalOpen}
          onOpenChange={setManualAccountReceivableModalOpen}
          manualInvoicesOnly
        />
      )}
    </>
  );
};
